
import React, { useRef, useState } from 'react';
import { 
  User, 
  Mail, 
  CalendarClock, 
  ShieldCheck, 
  Camera, 
  Edit2, 
  Building2, 
  MapPin, 
  Hash, 
  TrendingUp, 
  Save, 
  UserCheck, 
  X,
  Phone,
  Globe,
  Layers,
  Calendar
} from 'lucide-react';
import { Card, Badge } from './SharedComponents';
import { User as UserType, Company } from './types';

interface SettingsProfileProps {
  user: UserType;
  company?: Company;
  isSuper: boolean;
  onUpdate: (u: UserType) => void;
}

export const SettingsProfile = ({ user, company, isSuper, onUpdate }: SettingsProfileProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ name: user.name, email: user.email });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onUpdate({ ...user, avatar: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    onUpdate({ ...user, name: formData.name, email: formData.email });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({ name: user.name, email: user.email });
    setIsEditing(false);
  };

  return (
    <div className="space-y-10 animate-in slide-in-from-bottom-2 duration-500">
      
      {/* Card do Usuário (Sessão Atual) */}
      <Card className={`${isSuper ? 'bg-[#1e293b] border-white/5 p-12 text-white' : 'bg-white border-gray-100 p-12'} rounded-[48px] shadow-2xl relative overflow-hidden`}>
        <div className="flex flex-col md:flex-row items-center gap-10 mb-14 relative z-10">
          <div className="relative group">
             <div className={`w-40 h-40 rounded-[48px] flex items-center justify-center text-5xl font-black border-4 shadow-2xl overflow-hidden transition-all group-hover:brightness-50 ${isSuper ? 'bg-primary text-white border-white/10' : 'bg-secondary text-white border-white'}`}>
              {user.avatar ? <img src={user.avatar} className="w-full h-full object-cover" /> : user.name.charAt(0)}
            </div>
            <button onClick={() => fileInputRef.current?.click()} className="absolute inset-0 flex flex-col items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity z-20">
               <Camera size={32} />
               <span className="text-[10px] font-black uppercase mt-2 tracking-widest">Trocar Foto</span>
            </button>
            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
          </div>
          
          <div className="text-center md:text-left flex-1 space-y-2">
            {isEditing ? (
              <div className="space-y-4">
                 <input 
                  className={`text-4xl font-black uppercase tracking-tight bg-transparent border-b-4 border-primary outline-none w-full ${isSuper ? 'text-white' : 'text-secondary'}`}
                  value={formData.name}
                  autoFocus
                  onChange={e => setFormData({...formData, name: e.target.value})}
                />
              </div>
            ) : (
              <h3 className="text-4xl font-black uppercase tracking-tight leading-none">{user.name}</h3>
            )}
            <p className="text-primary font-black uppercase text-xs tracking-[0.4em] flex items-center justify-center md:justify-start gap-2">
              <ShieldCheck size={18} /> Acesso Nível {isSuper ? 'Master' : 'Gestor'}
            </p>
          </div>

          <div className="flex gap-3">
            {isEditing ? (
              <>
                <button 
                  onClick={handleCancel}
                  className="px-8 py-5 rounded-[24px] font-black uppercase text-[10px] tracking-widest flex items-center gap-2 bg-gray-100 text-gray-400 hover:bg-gray-200 transition-all"
                >
                  <X size={18} /> Cancelar
                </button>
                <button 
                  onClick={handleSave}
                  className="px-8 py-5 rounded-[24px] font-black uppercase text-[10px] tracking-widest flex items-center gap-2 bg-green-500 text-white shadow-xl shadow-green-100 hover:brightness-110 transition-all"
                >
                  <Save size={18} /> Salvar Agora
                </button>
              </>
            ) : (
              <button 
                onClick={() => setIsEditing(true)}
                className="px-10 py-5 rounded-[24px] font-black uppercase text-[10px] tracking-widest flex items-center gap-2 bg-primary text-white shadow-2xl shadow-primary/20 hover:brightness-110 transition-all active:scale-95"
              >
                <Edit2 size={18} /> Editar Perfil
              </button>
            )}
          </div>
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-2 gap-10 pt-12 border-t-2 ${isSuper ? 'border-white/5' : 'border-gray-50'}`}>
          <div className="space-y-3">
            <label className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">E-mail de Login</label>
            <div className={`p-6 rounded-[24px] border-2 font-bold flex items-center justify-between transition-all ${isEditing ? 'border-primary bg-white' : (isSuper ? 'bg-white/5 border-white/5 text-white/80' : 'bg-gray-50 border-gray-50 text-secondary')}`}>
              {isEditing ? (
                <input className="bg-transparent w-full outline-none text-secondary" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
              ) : (
                <div className="flex items-center gap-4 text-lg"><Mail size={22} className="text-primary" /> {user.email}</div>
              )}
            </div>
          </div>
          <div className="space-y-3">
            <label className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Ativo desde</label>
            <div className={`p-6 rounded-[24px] border-2 font-bold flex items-center gap-4 text-lg ${isSuper ? 'bg-white/5 border-white/5 text-white/80' : 'bg-gray-50 border-gray-50 text-secondary'}`}>
              <CalendarClock size={22} className="text-primary" /> {new Date(user.createdAt).toLocaleDateString('pt-BR')}
            </div>
          </div>
        </div>
      </Card>

      {/* Seção Dados Corporativos Completa (Apenas para Admin) */}
      {!isSuper && company && (
        <Card className="bg-white border-2 border-primary/5 p-12 rounded-[48px] shadow-2xl space-y-12 animate-in fade-in slide-in-from-bottom-4 delay-150">
           <div className="flex justify-between items-center border-b border-gray-100 pb-8">
              <div>
                 <h4 className="text-3xl font-black uppercase tracking-tight text-secondary italic">Infraestrutura Contratada</h4>
                 <p className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.3em] mt-1">Configuração provisionada pelo administrador central</p>
              </div>
              <div className="flex items-center gap-4">
                 <Badge className="bg-primary/10 text-primary border-none font-black text-[10px] px-6 py-3 tracking-[0.2em]">VERTICAL: {company.vertical}</Badge>
                 <div className="w-16 h-16 bg-primary/5 text-primary rounded-3xl flex items-center justify-center border-2 border-primary/10 shadow-inner overflow-hidden">
                    {company.logo ? (
                      <img src={company.logo} className="w-full h-full object-contain p-2" />
                    ) : (
                      <Building2 size={32} />
                    )}
                 </div>
              </div>
           </div>

           {/* Grid de Informações Jurídicas */}
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <CompanyMetric label="Razão Social" value={company.name} icon={Building2} />
              <CompanyMetric label="CNPJ Operacional" value={company.cnpj} icon={Hash} />
              <CompanyMetric label="Plano SaaS" value={company.plan} icon={TrendingUp} highlighted />
              <CompanyMetric label="Expiração Licença" value={company.licenseExpiry || '31/12/2025'} icon={Calendar} color="text-amber-600" />
           </div>

           {/* Seção de Contatos & Localização */}
           <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-[#f9fafb] p-8 rounded-[40px] border-2 border-gray-50 space-y-6">
                 <div className="flex items-center gap-4">
                    <div className="p-4 bg-white rounded-2xl shadow-md"><MapPin size={24} className="text-primary" /></div>
                    <div>
                       <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Sede Administrativa</p>
                       <p className="text-lg font-black text-secondary uppercase">{company.address}</p>
                    </div>
                 </div>
                 <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white p-4 rounded-2xl border border-gray-100">
                       <p className="text-[8px] font-black text-gray-300 uppercase mb-1">Bairro</p>
                       <p className="text-xs font-bold text-secondary uppercase">{company.neighborhood}</p>
                    </div>
                    <div className="bg-white p-4 rounded-2xl border border-gray-100">
                       <p className="text-[8px] font-black text-gray-300 uppercase mb-1">Cidade</p>
                       <p className="text-xs font-bold text-secondary uppercase">{company.city}</p>
                    </div>
                    <div className="bg-white p-4 rounded-2xl border border-gray-100">
                       <p className="text-[8px] font-black text-gray-300 uppercase mb-1">Estado</p>
                       <p className="text-xs font-bold text-secondary uppercase">{company.state}</p>
                    </div>
                 </div>
              </div>

              <div className="bg-secondary text-white p-8 rounded-[40px] shadow-2xl relative overflow-hidden">
                 <div className="absolute top-0 right-0 p-4 opacity-5 rotate-12"><Phone size={100} /></div>
                 <h5 className="text-[10px] font-black text-primary uppercase tracking-widest mb-6">Canais de Atendimento</h5>
                 <div className="space-y-6 relative z-10">
                    <div className="flex items-center gap-4">
                       <div className="p-3 bg-white/10 rounded-xl text-primary"><UserCheck size={20} /></div>
                       <div>
                          <p className="text-[8px] font-black text-white/40 uppercase">Gestor Master</p>
                          <p className="text-sm font-bold">{company.managerName}</p>
                       </div>
                    </div>
                    <div className="flex items-center gap-4">
                       <div className="p-3 bg-white/10 rounded-xl text-primary"><Phone size={20} /></div>
                       <div>
                          <p className="text-[8px] font-black text-white/40 uppercase">WhatsApp Corporativo</p>
                          <p className="text-sm font-bold">{company.whatsapp}</p>
                       </div>
                    </div>
                    <div className="flex items-center gap-4">
                       <div className="p-3 bg-white/10 rounded-xl text-primary"><Mail size={20} /></div>
                       <div>
                          <p className="text-[8px] font-black text-white/40 uppercase">E-mail Oficial</p>
                          <p className="text-xs font-bold truncate max-w-[150px]">{company.contactEmail}</p>
                       </div>
                    </div>
                 </div>
              </div>
           </div>
        </Card>
      )}
    </div>
  );
};

const CompanyMetric = ({ label, value, icon: Icon, highlighted, color }: any) => (
  <div className={`p-6 rounded-[32px] border-2 transition-all flex flex-col gap-4 ${highlighted ? 'bg-primary border-primary text-white shadow-xl shadow-primary/20' : 'bg-gray-50 border-gray-100 hover:border-primary/20'}`}>
     <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${highlighted ? 'bg-white/20' : 'bg-white text-gray-400 shadow-sm'}`}>
        <Icon size={18} />
     </div>
     <div>
        <p className={`text-[8px] font-black uppercase tracking-[0.2em] mb-1 ${highlighted ? 'text-white/60' : 'text-gray-400'}`}>{label}</p>
        <p className={`text-sm font-black uppercase tracking-tight truncate ${highlighted ? 'text-white' : (color || 'text-secondary')}`}>{value}</p>
     </div>
  </div>
);
